import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';

const Profile = ({ user, setUser, onLogout }) => {
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: ''
  });
  const [interval, setInterval] = useState(user.checkin_interval_hours);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/emergency-contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/emergency-contacts`, newContact);
      toast.success('Emergency contact added');
      setNewContact({ name: '', email: '', phone: '', relationship: '' });
      setShowAddForm(false);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/emergency-contacts/${contactId}`);
      toast.success('Contact removed');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleUpdateInterval = async () => {
    try {
      await axios.put(`${API}/user/settings`, {
        checkin_interval_hours: parseInt(interval)
      });
      toast.success('Settings updated');
      
      // Update local user data
      const updatedUser = { ...user, checkin_interval_hours: parseInt(interval) };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  return (
    <div className="dashboard-container" data-testid="profile-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="page-title">Profile Settings</h1>

        {/* User Info Section */}
        <div className="section-card" data-testid="user-info-section">
          <h2 className="section-title">Your Information</h2>
          <div className="form-group">
            <label>Name</label>
            <input type="text" value={user.name} disabled />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={user.email} disabled />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="text" value={user.phone || 'Not provided'} disabled />
          </div>
          <div className="form-group">
            <label>Age</label>
            <input type="text" value={user.age || 'Not provided'} disabled />
          </div>
        </div>

        {/* Client/Patient Information */}
        <div className="section-card" data-testid="client-info-section">
          <h2 className="section-title">Client/Patient Information</h2>
          <div className="form-group">
            <label htmlFor="client-name">Client Name</label>
            <input
              id="client-name"
              type="text"
              value={user.client_name || 'Not provided'}
              disabled
              data-testid="client-name-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="client-address">Client Address</label>
            <textarea
              id="client-address"
              rows="2"
              value={user.client_address || 'Not provided'}
              disabled
              data-testid="client-address-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="client-age">Client Age</label>
            <input
              id="client-age"
              type="text"
              value={user.client_age || 'Not provided'}
              disabled
              data-testid="client-age-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="client-health">Client Health Condition</label>
            <textarea
              id="client-health"
              rows="3"
              value={user.client_health_condition || 'Not provided'}
              disabled
              data-testid="client-health-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="client-emergency">Client Emergency Contacts</label>
            <textarea
              id="client-emergency"
              rows="3"
              value={user.client_emergency_contacts || 'Not provided'}
              disabled
              data-testid="client-emergency-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="client-history">Client History & Care Notes</label>
            <textarea
              id="client-history"
              rows="4"
              value={user.client_history || 'Not provided'}
              disabled
              data-testid="client-history-display"
            />
          </div>
        </div>

        {/* Medical & Health Information */}
        <div className="section-card" data-testid="health-info-section">
          <h2 className="section-title">Medical & Health Information</h2>
          <div className="form-group">
            <label htmlFor="medical-conditions">Your Medical Conditions</label>
            <textarea
              id="medical-conditions"
              rows="3"
              value={user.medical_conditions || ''}
              placeholder="Any medical conditions..."
              disabled
              data-testid="medical-conditions-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="health-issues">Current Health Issues</label>
            <textarea
              id="health-issues"
              rows="3"
              value={user.health_issues || ''}
              placeholder="Any current health concerns..."
              disabled
              data-testid="health-issues-display"
            />
          </div>
          <div className="form-group">
            <label htmlFor="special-instructions">Special Instructions</label>
            <textarea
              id="special-instructions"
              rows="4"
              value={user.special_instructions || ''}
              placeholder="Important information for emergency contacts..."
              disabled
              data-testid="special-instructions-display"
            />
          </div>
          <button 
            className="submit-button" 
            onClick={() => window.location.href = '/profile/edit'}
            data-testid="edit-profile-button"
            style={{ marginTop: '1rem' }}
          >
            Edit Profile Information
          </button>
        </div>

        {/* Social Media */}
        {(user.social_facebook || user.social_x || user.social_instagram || user.social_linkedin || user.social_youtube || user.social_tiktok || user.social_other) && (
          <div className="section-card" data-testid="social-media-section">
            <h2 className="section-title"><i className="fa-solid fa-share-nodes" style={{ marginRight: '8px' }}></i> Social Media</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {user.social_facebook && (
                <a href={user.social_facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#e7f3ff', color: '#1877f2', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-facebook"></i> Facebook
                </a>
              )}
              {user.social_x && (
                <a href={user.social_x} target="_blank" rel="noopener noreferrer" data-testid="social-x-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#f1f5f9', color: '#0f172a', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-x-twitter"></i> X
                </a>
              )}
              {user.social_instagram && (
                <a href={user.social_instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#fce7f3', color: '#e4405f', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-instagram"></i> Instagram
                </a>
              )}
              {user.social_linkedin && (
                <a href={user.social_linkedin} target="_blank" rel="noopener noreferrer" data-testid="social-linkedin-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#e0f2fe', color: '#0a66c2', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-linkedin"></i> LinkedIn
                </a>
              )}
              {user.social_youtube && (
                <a href={user.social_youtube} target="_blank" rel="noopener noreferrer" data-testid="social-youtube-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#fee2e2', color: '#ff0000', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-youtube"></i> YouTube
                </a>
              )}
              {user.social_tiktok && (
                <a href={user.social_tiktok} target="_blank" rel="noopener noreferrer" data-testid="social-tiktok-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#f1f5f9', color: '#0f172a', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-brands fa-tiktok"></i> TikTok
                </a>
              )}
              {user.social_other && (
                <a href={user.social_other} target="_blank" rel="noopener noreferrer" data-testid="social-other-link"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '0.5rem 1rem', borderRadius: '8px', background: '#f1f5f9', color: '#2c5364', textDecoration: 'none', fontWeight: '600', fontSize: '0.9rem' }}>
                  <i className="fa-solid fa-link"></i> Website
                </a>
              )}
            </div>
          </div>
        )}

        {/* Check-in Settings */}
        <div className="section-card" data-testid="settings-section">
          <h2 className="section-title">Check-in Settings</h2>
          <div className="form-group">
            <label htmlFor="interval">Check-in Frequency</label>
            <select
              id="interval"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              data-testid="interval-select"
            >
              <option value="24">Every 24 hours</option>
              <option value="48">Every 2 days</option>
              <option value="72">Every 3 days</option>
              <option value="168">Weekly</option>
              <option value="336">Every 2 weeks</option>
            </select>
          </div>
          <button 
            className="submit-button" 
            onClick={handleUpdateInterval}
            data-testid="update-settings-button"
          >
            Update Check-in Frequency
          </button>
        </div>

        {/* Emergency Contacts */}
        <div className="section-card" data-testid="contacts-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 className="section-title" style={{ marginBottom: 0 }}>Emergency Contacts</h2>
            <button 
              className="submit-button" 
              style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
              onClick={() => setShowAddForm(!showAddForm)}
              data-testid="add-contact-toggle"
            >
              {showAddForm ? 'Cancel' : 'Add Contact'}
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddContact} style={{ marginBottom: '2rem' }} data-testid="add-contact-form">
              <div className="form-group">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  type="text"
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  required
                  data-testid="contact-email-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone">Phone (Optional)</label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact-relationship">Relationship</label>
                <input
                  id="contact-relationship"
                  type="text"
                  value={newContact.relationship}
                  onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                  placeholder="e.g., Daughter, Friend, Neighbor"
                  required
                  data-testid="contact-relationship-input"
                />
              </div>
              <button type="submit" className="submit-button" data-testid="save-contact-button">
                Save Contact
              </button>
            </form>
          )}

          {loading ? (
            <p>Loading contacts...</p>
          ) : contacts.length === 0 ? (
            <div className="empty-state" data-testid="empty-contacts">
              <div className="empty-state-icon">📋</div>
              <p>No emergency contacts added yet</p>
            </div>
          ) : (
            <div className="contact-list">
              {contacts.map((contact) => (
                <div key={contact.id} className="contact-item" data-testid={`contact-item-${contact.id}`}>
                  <div className="contact-info">
                    <h4>{contact.name}</h4>
                    <p>{contact.email} • {contact.relationship}</p>
                    {contact.phone && <p>📞 {contact.phone}</p>}
                  </div>
                  <button 
                    className="delete-button"
                    onClick={() => handleDeleteContact(contact.id)}
                    data-testid={`delete-contact-${contact.id}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;